/**
 * Remembers the last click on the page between the bundle running and the form mounting.
 *
 * The form mounts on window load, and only then knows its triggerSelector, so a visitor who
 * clicks the customer's "Schedule" button in that gap would otherwise get nothing and think the
 * button is dead. Form asks for that click on mount and opens if it matched.
 */

// Long enough for a slow page's load event, short enough that a click the visitor has
// forgotten about does not pop the form open later.
export const EARLY_CLICK_MAX_AGE_MS = 10000;

let lastClick: { readonly target: Element; readonly at: number } | undefined;

let recording = false;

const record = (event: MouseEvent) => {
    if (event.target instanceof Element) {
        lastClick = { target: event.target, at: Date.now() };
    }
};

export function startRecordingEarlyClicks(): void {
    if (recording) {
        return;
    }
    recording = true;
    // Capture phase, so a host page handler that stops propagation cannot hide the click.
    document.addEventListener("click", record, true);
}

/**
 * Stops recording and returns the last recorded click target, if it is recent enough.
 */
export function takeEarlyClick(): Element | undefined {
    document.removeEventListener("click", record, true);
    recording = false;

    const click = lastClick;
    lastClick = undefined;

    if (!click || Date.now() - click.at > EARLY_CLICK_MAX_AGE_MS) {
        return undefined;
    }
    return click.target;
}
